import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../authContext";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaWhatsapp, FaEnvelope, FaShieldAlt, FaBolt, FaHeadset } from "react-icons/fa";
import { LuX } from "react-icons/lu";

const INITIAL_FORM_DATA = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  phoneNumber: "",
  city: "",
};

const LOGIN_METHOD_OPTIONS = [
  {
    value: "otp",
    label: "WhatsApp Login",
  },
  {
    value: "password",
    label: "Email Login",
  },
];

const DEFAULT_LOGIN_METHOD = "otp";

const FormInput = ({
  name,
  type = "text",
  value,
  onChange,
  error,
  placeholder,
  disabled = false,
  isPassword = false,
  theme = "light",
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isDark = theme === "dark";
  
  return (
    <div>
      <div className="relative">
        <input
          type={isPassword ? (showPassword ? "text" : "password") : type}
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={e => {
            if (name === "phoneNumber" && (!/\d/.test(e.key) || e.target.value.length >= 10) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
              e.preventDefault();
              return;
            }
            if ((name === "name" || name === "city") && /\d/.test(e.key)) {
              e.preventDefault();
              return;
            }
          }}
          className={`w-full rounded-xl border px-3 py-2 text-sm font-medium shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 focus:outline-none focus:ring-2 ${
            isDark
              ? `${error ? "border-red-500 focus:ring-red-500/20" : "border-white/20 focus:border-white/40 focus:ring-white/10"} bg-white/10 text-white placeholder:text-gray-300`
              : `${error ? "border-red-500 focus:ring-red-500/15" : "border-[#e7dfd5] focus:border-[#111111] focus:ring-black/5"} bg-[#fbfaf8] text-gray-900 placeholder:text-gray-400`
          } ${isPassword ? "pr-10" : ""}`}
          placeholder={placeholder}
          required
          disabled={disabled}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={`absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none ${
              isDark ? "text-gray-300 hover:text-white" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        )}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

const RegisterModal = ({ isOpen, toggle, initialMode = "login", onLoginSuccess }) => {
  const { login, register, requestLoginOtp, retryLoginOtp, verifyLoginOtp } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loginMethod, setLoginMethod] = useState(DEFAULT_LOGIN_METHOD);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpOrderId, setOtpOrderId] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setIsLogin(initialMode !== "register");
    setLoginMethod(DEFAULT_LOGIN_METHOD);
    setIsForgotPassword(false);
    setLoading(false);
    setErrors({});
    setFormData(INITIAL_FORM_DATA);
    setForgotPasswordEmail("");
    setShowOtpInput(false);
    setOtp("");
    setOtpOrderId("");
  }, [isOpen, initialMode]);

  const validations = {
    name: (v) => v.length < 2 ? 'Name should be at least 2 characters long' : '',
    email: (v) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Please enter a valid email address' : '',
    phoneNumber: (v) => !/^\d{10}$/.test(v) ? 'Phone number should be 10 digits' : '',
    city: (v) => v.length < 2 ? 'City should be at least 2 characters long' : '',
    password: (v) => '',
    confirmPassword: (v) => v !== formData.password ? 'Passwords do not match' : ''
  };

  const validateForm = () => {
    const newErrors = {};

    if (isLogin && loginMethod === "otp") {
      const phoneError = validations.phoneNumber(formData.phoneNumber);
      if (phoneError) newErrors.phoneNumber = phoneError;
      if (showOtpInput && !/^\d{4,8}$/.test(otp)) {
        newErrors.otp = "Please enter the OTP sent to your WhatsApp number";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    Object.keys(formData).forEach(key => {
      if (!isLogin || !['name', 'phoneNumber', 'city', 'confirmPassword'].includes(key)) {
        const error = validations[key]?.(formData[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setLoading(true);
    try {
      const result = isLogin
        ? await login(formData.email, formData.password)
        : await register({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            phoneNumber: formData.phoneNumber,
            city: formData.city,
          });

      if (result.success) {
        toast.success(isLogin ? "Logged in successfully!" : "Registered successfully! Please login.");
        if (isLogin) {
          if (onLoginSuccess) onLoginSuccess();
          else toggle();
        } else {
          setIsLogin(true);
          setFormData(INITIAL_FORM_DATA);
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const resetOtpState = () => {
    setShowOtpInput(false);
    setOtp("");
    setOtpOrderId("");
    setErrors(prev => ({
      ...prev,
      phoneNumber: "",
      otp: "",
    }));
  };

  const handleOtpRequest = async (e) => {
    e.preventDefault();

    const phoneError = validations.phoneNumber(formData.phoneNumber);
    if (phoneError) {
      setErrors(prev => ({ ...prev, phoneNumber: phoneError }));
      toast.error(phoneError);
      return;
    }

    setLoading(true);
    setErrors(prev => ({ ...prev, phoneNumber: "", otp: "" }));

    try {
      const result = await requestLoginOtp(formData.phoneNumber);

      if (result.success) {
        setOtpOrderId(result.orderId || "");
        setShowOtpInput(true);
        toast.success(result.message || "OTP sent successfully on WhatsApp.");
      } else {
        throw new Error(result.message || "Failed to send OTP.");
      }
    } catch (error) {
      const message = error.message || "Failed to send OTP.";
      setErrors(prev => ({ ...prev, phoneNumber: message }));
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    setErrors(prev => ({ ...prev, otp: "" }));

    try {
      const result = await verifyLoginOtp({
        orderId: otpOrderId,
        otp,
        phoneNumber: formData.phoneNumber,
      });

      if (result.success) {
        toast.success("Logged in successfully!");
        if (onLoginSuccess) onLoginSuccess();
        else toggle();
      } else {
        throw new Error(result.message || "Invalid OTP.");
      }
    } catch (error) {
      const message = error.message || "Invalid OTP.";
      setErrors(prev => ({ ...prev, otp: message }));
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpRetry = async () => {
    const phoneError = validations.phoneNumber(formData.phoneNumber);
    if (phoneError) {
      setErrors(prev => ({ ...prev, phoneNumber: phoneError }));
      toast.error(phoneError);
      return;
    }

    setLoading(true);
    setErrors(prev => ({ ...prev, otp: "" }));

    try {
      const result = await retryLoginOtp({
        orderId: otpOrderId,
        phoneNumber: formData.phoneNumber,
      });

      if (result.success) {
        setOtpOrderId(result.orderId || otpOrderId);
        toast.success(result.message || "OTP resent successfully on WhatsApp.");
      } else {
        throw new Error(result.message || "Failed to resend OTP.");
      }
    } catch (error) {
      const message = error.message || "Failed to resend OTP.";
      setErrors(prev => ({ ...prev, otp: message }));
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate phone number length
    if (name === "phoneNumber" && (!/^\d*$/.test(value) || value.length > 10)) {
      return;
    }
    
    // Validate name and city to only allow letters and spaces
    if ((name === "name" || name === "city") && !/^[a-zA-Z\s]*$/.test(value)) {
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));

    const error = validations[name]?.(value);
    if (error) setErrors(prev => ({ ...prev, [name]: error }));
    
    if (name === 'password' && formData.confirmPassword) {
      const confirmError = validations.confirmPassword(formData.confirmPassword);
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/forgot-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: forgotPasswordEmail
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Password reset instructions have been sent to your email');
        setForgotPasswordEmail("");
        setIsForgotPassword(false);
        setIsLogin(true);
      } else {
        throw new Error(data.message || 'Failed to process request');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process forgot password request');
    } finally {
      setLoading(false);
    }
  };

  const renderForgotPasswordForm = () => (
    <div className="w-full bg-white px-5 pb-5 pt-14 text-gray-900 sm:px-6 sm:pb-6 sm:pt-14 md:w-[48%] md:px-6 md:pb-6 md:pt-10">
      <h3 className="mb-4 text-2xl font-bold text-gray-900">Reset Your Password</h3>
      <form onSubmit={handleForgotPassword} className="space-y-4">
        <FormInput
          name="forgotPasswordEmail"
          type="email"
          value={forgotPasswordEmail}
          onChange={(e) => setForgotPasswordEmail(e.target.value)}
          placeholder="Enter your email address"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Processing...
            </div>
          ) : (
            "Reset Password"
          )}
        </button>

        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => {
              setIsForgotPassword(false);
              setForgotPasswordEmail("");
            }}
            className="inline-flex items-center justify-center text-sm font-semibold text-[#111111] transition-colors hover:text-red-600"
          >
            Back to {isLogin ? "Login" : "Register"}
          </button>
        </div>
      </form>
    </div>
  );

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto z-[99999]" role="dialog" aria-modal="true">
      <div className="flex min-h-screen items-center justify-center p-3 sm:p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={toggle}></div>
        <div className="relative w-full max-w-[880px] overflow-hidden rounded-[24px] bg-white shadow-xl transition-all max-h-[calc(100vh-1.5rem)] overflow-y-auto sm:max-h-[calc(100vh-2rem)] z-10">
          <button type="button" aria-label="Close login form" onClick={toggle} className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black text-white shadow-md transition-transform hover:scale-105 sm:right-5 sm:top-5">
            <LuX className="text-lg" />
          </button>

          <div className="flex flex-col md:flex-row">
            <div className="hidden bg-brand-black p-8 text-white md:flex md:w-[52%] md:items-center md:justify-center lg:p-10">
              <div className="mx-auto flex w-full max-w-md flex-col items-center justify-center text-center">
                <div className="mb-6 flex items-center justify-center">
                  <img src="/in_logo.jpg" alt="Indorinama Logo" className="w-40 lg:w-48" />
                </div>
                <h2 className="mb-3 text-center text-2xl font-bold">Welcome to Indorinama</h2>
                <div className="mb-6 flex flex-col items-center justify-center opacity-90">
                  <p className="text-center font-serif text-[15px] italic leading-tight tracking-wide text-gray-200">
                    कर्मण्येवाधिकारस्ते मा फलेषु कदाचन।<br />
                    मा कर्मफलहेतुर्भूर्मा ते सङ्गोऽस्त्वकर्मणि॥
                  </p>
                </div>

              </div>
            </div>

            {isForgotPassword ? renderForgotPasswordForm() : (
              <div className="w-full bg-white px-4 pb-4 pt-10 text-gray-900 transition-colors sm:px-6 sm:pb-6 sm:pt-14 md:w-[48%] md:px-5 md:pb-5 md:pt-10">
                <h3 className="mb-3 text-xl font-bold text-gray-900 sm:mb-3.5 sm:text-2xl">
                  {isLogin ? <><span className="text-red-600">Login</span> to Your Account</> : "Create Your Account"}
                </h3>

                {isLogin && (
                  <div className="mb-3">
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                      Choose Login Method
                    </p>
                    <div className="grid grid-cols-2 gap-0.5 rounded-[16px] bg-[#f4f1ec] p-0.5">
                      {LOGIN_METHOD_OPTIONS.map((option) => {
                        const isActive = loginMethod === option.value;
                        const isWhatsapp = option.value === "otp";

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              if (option.value === "password") {
                                setLoginMethod("password");
                                resetOtpState();
                                return;
                              }

                              setLoginMethod("otp");
                              setFormData(prev => ({ ...prev, email: "", password: "" }));
                              resetOtpState();
                            }}
                            className={`rounded-[12px] px-1.5 py-1.5 text-left transition-all sm:px-2 ${
                              isActive
                                ? isWhatsapp
                                  ? "bg-red-600 text-white shadow-[0_10px_24px_rgba(220,38,38,0.24)]"
                                  : "bg-[#111111] text-white shadow-[0_10px_24px_rgba(17,17,17,0.12)]"
                                : "bg-transparent text-gray-600 hover:text-gray-900"
                            }`}
                          >
                            <span className="flex min-h-[28px] items-center justify-center gap-1 text-center">
                              {isWhatsapp ? (
                                <FaWhatsapp className={`text-[13px] ${isActive ? "text-white" : "text-red-600"}`} />
                              ) : (
                                <FaEnvelope className={`text-[13px] ${isActive ? "text-white" : "text-gray-600"}`} />
                              )}
                              <span className="block text-[11px] font-semibold leading-tight sm:text-[12px]">
                                {option.label}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <form
                  onSubmit={
                    isLogin && loginMethod === "otp"
                      ? (showOtpInput ? handleOtpVerification : handleOtpRequest)
                      : handleSubmit
                  }
                  className="space-y-2"
                >
                  {!isLogin && (
                    <>
                      <FormInput name="name" value={formData.name} onChange={handleInputChange} error={errors.name} placeholder="Full Name" theme="light" />
                      <FormInput name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={handleInputChange} error={errors.phoneNumber} placeholder="Phone Number" theme="light" />
                      <FormInput name="city" value={formData.city} onChange={handleInputChange} error={errors.city} placeholder="City" theme="light" />
                    </>
                  )}

                  {isLogin && loginMethod === "otp" ? (
                    <>
                      <FormInput
                        name="phoneNumber"
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        error={errors.phoneNumber}
                        placeholder="WhatsApp Mobile Number"
                        theme="light"
                        disabled={loading || showOtpInput}
                      />
                      {showOtpInput && (
                        <FormInput
                          name="otp"
                          type="text"
                          value={otp}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "").slice(0, 8);
                            setOtp(value);
                            setErrors(prev => ({ ...prev, otp: "" }));
                          }}
                          error={errors.otp}
                          placeholder="Enter WhatsApp Code"
                          theme="light"
                          disabled={loading}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <FormInput 
                        name="email" 
                        type="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        error={errors.email} 
                        placeholder="Email Address" 
                        theme="light"
                      />
                      <FormInput 
                        name="password" 
                        value={formData.password} 
                        onChange={handleInputChange} 
                        error={errors.password} 
                        placeholder="Password" 
                        isPassword={true}
                        theme="light"
                      />
                    </>
                  )}

                  {!isLogin && (
                    <FormInput 
                      name="confirmPassword" 
                      value={formData.confirmPassword} 
                      onChange={handleInputChange} 
                      error={errors.confirmPassword} 
                      placeholder="Confirm Password" 
                      isPassword={true}
                      theme="light"
                    />
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 bg-red-600 hover:bg-red-700 focus:ring-red-500"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        {isLogin
                          ? loginMethod === "otp"
                            ? showOtpInput
                              ? "Verifying OTP..."
                              : "Sending OTP..."
                            : "Logging in..."
                          : "Creating account..."}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        {isLogin && loginMethod === "otp" && !showOtpInput && (
                          <FaWhatsapp className="text-base" />
                        )}
                        <span>
                          {isLogin
                            ? loginMethod === "otp"
                              ? showOtpInput
                                ? "Verify WhatsApp Code"
                                : "Send Code on WhatsApp"
                              : "Login"
                            : "Create Account"}
                        </span>
                      </div>
                    )}
                  </button>

                  {isLogin && loginMethod === "otp" && showOtpInput && (
                    <button
                      type="button"
                      onClick={handleOtpRetry}
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-600/30 bg-red-600/8 py-2.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-600/14 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:opacity-50"
                    >
                      <FaWhatsapp className="text-base" />
                      Resend Code on WhatsApp
                    </button>
                  )}

                  <div className="mt-2 border-t border-[#efe7dd] pt-3 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setLoginMethod(DEFAULT_LOGIN_METHOD);
                        setErrors({});
                        setFormData(INITIAL_FORM_DATA);
                        setIsForgotPassword(false);
                        resetOtpState();
                      }}
                      className="inline-flex items-center justify-center text-sm font-semibold text-[#111111] transition-colors hover:text-red-600"
                    >
                      {isLogin ? "Create Your Account" : "Back to Login"}
                    </button>
                    
                    {isLogin && loginMethod === "password" && (
                      <div className="mt-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setIsForgotPassword(true);
                            setErrors({});
                          }}
                          className="inline-flex items-center justify-center text-sm font-medium text-[#6b6258] transition-colors hover:text-[#111111]"
                        >
                          Forgot Password?
                        </button>
                      </div>
                    )}
                  </div>
                </form>

                {isLogin && (
                  <div className="mt-8 flex w-full items-center justify-center">
                    {/* Item 1 */}
                    <div className="flex flex-1 flex-col items-center px-1">
                      <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg">
                        <FaShieldAlt className="text-[13px]" />
                      </div>
                      <h4 className="mb-0.5 text-[11px] font-semibold leading-tight text-gray-800 text-center">Secure and safe</h4>
                      <p className="text-[9px] leading-snug text-gray-500 text-center">Your data is protected</p>
                    </div>
                    
                    {/* Divider */}
                    <div className="h-8 w-px bg-gray-200"></div>
                    
                    {/* Item 2 */}
                    <div className="flex flex-1 flex-col items-center px-1">
                      <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg">
                        <FaBolt className="text-[13px]" />
                      </div>
                      <h4 className="mb-0.5 text-[11px] font-semibold leading-tight text-gray-800 text-center">Quick and Easy</h4>
                      <p className="text-[9px] leading-snug text-gray-500 text-center">Login in just a few steps</p>
                    </div>

                    {/* Divider */}
                    <div className="h-8 w-px bg-gray-200"></div>
                    
                    {/* Item 3 */}
                    <div className="flex flex-1 flex-col items-center px-1">
                      <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg">
                        <FaHeadset className="text-[13px]" />
                      </div>
                      <h4 className="mb-0.5 text-[11px] font-semibold leading-tight text-gray-800 text-center">Call Support</h4>
                      <p className="text-[9px] leading-snug text-gray-500 text-center">We're here to help you</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RegisterModal;
